import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { ScanDay } from '../api/types';
import { count, shortDate } from '../utils/format';

import { ChartTooltip } from './ChartTooltip';
import { Legend } from './Legend';
import { CHROME, STATUS } from './palette';

/**
 * Scans per day, split by outcome. Answers the question the age distribution
 * cannot: is the staleness being worked off, or is the crawler running and
 * achieving nothing? Outcome is a state rather than a category, so it wears the
 * reserved status colours, and the legend names both so colour is never the only
 * carrier.
 */
export function ScanActivityChart({ days }: { days: ScanDay[] }) {
  const total = days.reduce((sum, day) => sum + day.succeeded + day.failed, 0);

  if (total === 0) {
    return <div className="state">No scans recorded in this period.</div>;
  }

  return (
    <>
      <ResponsiveContainer width='100%' height={200}>
        <BarChart
          data={days}
          margin={{ top: 8, right: 8, bottom: 4, left: 0 }}
          barCategoryGap={2}
        >
          <CartesianGrid stroke={CHROME.grid} vertical={false} />
          <XAxis
            dataKey="day"
            tickFormatter={shortDate}
            tick={{ fill: CHROME.inkMuted, fontSize: 10 }}
            axisLine={{ stroke: CHROME.axis }}
            tickLine={false}
            minTickGap={24}
          />
          <YAxis
            tick={{ fill: CHROME.inkMuted, fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            width={38}
            allowDecimals={false}
          />
          <Tooltip
            cursor={{ fill: 'rgba(255,255,255,0.05)' }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) {
                return null;
              }

              const row = payload[0].payload as ScanDay;

              return (
                <ChartTooltip
                  title={shortDate(row.day)}
                  rows={[
                    {
                      key: 'ok',
                      label: 'Succeeded',
                      value: count(row.succeeded),
                      color: STATUS.good,
                    },
                    {
                      key: 'bad',
                      label: 'Failed',
                      value: count(row.failed),
                      color: STATUS.critical,
                    },
                    {
                      key: 'products',
                      label: 'Products touched',
                      value: count(row.products),
                    },
                  ]}
                />
              );
            }}
          />
          {/* A 2px surface gap keeps the two stacked segments from reading as one block. */}
          <Bar
            isAnimationActive={false}
            dataKey="succeeded"
            stackId="scans"
            fill={STATUS.good}
            stroke={CHROME.surface}
            strokeWidth={1}
            maxBarSize={26}
          />
          <Bar
            isAnimationActive={false}
            dataKey="failed"
            stackId="scans"
            fill={STATUS.critical}
            stroke={CHROME.surface}
            strokeWidth={1}
            radius={[4, 4, 0, 0]}
            maxBarSize={26}
          />
        </BarChart>
      </ResponsiveContainer>

      <Legend
        entries={[
          { label: 'Succeeded', color: STATUS.good },
          { label: 'Failed, blocked or no match', color: STATUS.critical },
        ]}
      />
    </>
  );
}
